import { GeometryUtils, isDefined, SimplePointCloud } from "../../infra3d";
import { FeatureStyle, GEOMETRYTYPE } from "../featuretree/internal";
import { USERINPUTTYPE } from "../userinput/internal";
import { Tool } from "./internal";
/**
 * Point Cloud Viewer Tool
 * @class PointCloudViewTool
 * @extends {Tool}
 */
export class PointCloudViewTool extends Tool {
    constructor(scene, toolhandler, options, name, group) {
        super(scene, toolhandler);
        this.tempcoordinates = [];
        this.coordinates = [];
        this.toolstate = -1;
        // event variables
        this.mousemoveeventnr = null;
        this.isloading = false;
        this.mousemoveactive = false;
        this.currentcursor = "";
        this.framecoordinates = [];
        this.actualPointCloud = null;
        this.name = name;
        this.group = group;
        //options - set the default values here....
        const layerstyle = new FeatureStyle({
            stylename: "DrawPolygonStyle",
            rules: [
                {
                    filter: "",
                    symbolizer: "point",
                    params: {
                        size: 4,
                        stroke: [0, 1, 0, 1],
                        "vertex-colors": true,
                    },
                },
                {
                    filter: "",
                    symbolizer: "line",
                    params: {
                        "stroke-width": 4,
                        stroke: [1, 0, 1, 1],
                    },
                },
                {
                    filter: "",
                    symbolizer: "polygon",
                    params: {
                        fill: [1, 0, 0, 0.7],
                    },
                },
            ],
        });
        //options - set the default values here....
        const defaultoptions = {
            layerstyle: layerstyle,
            enablecbf: null,
            disablecbf: null,
            activecursor: "crosshair",
            threshdistance: 0.4,
            maxdist: 20,
            quality: true,
        };
        for (const property in defaultoptions) {
            this.options[property] = defaultoptions[property];
        }
        super.parseOptions(options);
        //create the drawing layers
        this.featurecollection = this.scene
            .getFeatureHandler()
            .createFeatureCollection(this.options.layerstyle, null, "mmPointCloudViewTool_internal", false);
        this.multipointfeature = this.featurecollection.createFeature();
        this.multipointfeature.geometry = this.multipointfeature.createGeometry(GEOMETRYTYPE.MULTIPOINT);
        this.featurecollectionframe = this.scene
            .getFeatureHandler()
            .createFeatureCollection(this.options.layerstyle, null, "mmPointCloudViewTool_internal2", false);
        this.multipointfeatureframe = this.featurecollectionframe.createFeature();
        this.multipointfeatureframe.geometry = this.multipointfeatureframe.createGeometry(GEOMETRYTYPE.MULTIPOINT);
        this.type = "PointCloudViewTool";
    }
    /**
     * internal function - to activate tool call mmSelectFeatureTool.enable
     * @method _enableTool
     * @protected
     */
    _enableTool() {
        this.mousemoveeventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.MOUSEMOVE, this._onMouseMove, this);
        if (this.options.enablecbf != null) {
            this.on(PointCloudViewTool.enabled, this.options.enablecbf);
        }
        if (this.options.disablecbf != null) {
            this.on(PointCloudViewTool.disabled, this.options.disablecbf);
        }
        this.fire(PointCloudViewTool.enabled, this.options["cbobject"]);
        this.toolstate = 0;
        this.scene.getCursorStyleHandler().setCursorStyle(this.options.activecursor);
    }
    /**
     * internal function - to disable tool call mmSelectFeatureTool.disable
     * @method _disableTool
     * @protected
     */
    _disableTool() {
        // if tool is not active there is nothing to disable...
        if (!this.enabled)
            return;
        if (this.actualPointCloud) {
            this.scene.infrascene.remove(this.actualPointCloud);
        }
        this.reset();
        this.toolstate = -1;
        this.fire(PointCloudViewTool.disabled, this.options["cbobject"]);
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.MOUSEMOVE, this.mousemoveeventnr);
        this.mousemoveeventnr = null;
        if (this.options.enablecbf != null) {
            this.off(PointCloudViewTool.enabled, this.options.enablecbf);
        }
        if (this.options.disablecbf != null) {
            this.off(PointCloudViewTool.disabled, this.options.disablecbf);
        }
    }
    /**
     * internal function - onmouse move the pc-view will be updated
     * @method _onMouseMove
     * @param evt
     * @private
     */
    _onMouseMove(evt) {
        if (this.mousemoveactive) {
            this.currentcursor = this.engine.getCanvas().style.cursor;
            this.scene.getCursorStyleHandler().setCursorStyle("wait");
            return;
        }
        this.scene.getCursorStyleHandler().setCursorStyle(this.currentcursor);
        this.mousemoveactive = true;
        const mx = evt.offsetX;
        const my = evt.offsetY;
        this.coordinates = [];
        this.framecoordinates = [];
        let thresh = -0.1 * this.scene.infrascene.traversalState.zoom + 0.4;
        thresh = Math.max(thresh, 0.1);
        thresh = Math.max(thresh, this.options.threshdistance);
        const refpos = this.scene
            .getMeasureHandler()
            .createMonoplotMeasurePoint(this.scene.getCurrentGeoframe(), mx, my, this.options.maxdist);
        if (refpos == undefined ||
            !isFinite(refpos.Easting) ||
            !isFinite(refpos.Northing) ||
            !isFinite(refpos.Height)) {
            this.coordinates = [];
            this._draw(this.coordinates);
            this.mousemoveactive = false;
            return;
        }
        let i = -1;
        let j = -1;
        const limit = Math.round((-2.5 * refpos.distance + 100) * this.scene.infrascene.traversalState.zoom);
        const increment = Math.max(1, Math.round(this.scene.infrascene.traversalState.zoom / 2));
        let pos = null;
        let p = 0;
        for (i = 0; i < limit; i += increment) {
            p = 0;
            for (j = 0; j < limit; j += increment) {
                pos = this.scene
                    .getMeasureHandler()
                    .createMonoplotMeasurePoint(this.scene.getCurrentGeoframe(), mx + i, my + j, this.options.maxdist);
                if (GeometryUtils.CalcDistance(refpos, pos) < thresh && !pos.warning) {
                    this.coordinates.push(pos);
                    p += 1;
                }
            }
            if (p == 0)
                break;
        }
        for (i = 0; i < limit; i += increment) {
            p = 0;
            for (j = 0; j < limit; j += increment) {
                pos = this.scene
                    .getMeasureHandler()
                    .createMonoplotMeasurePoint(this.scene.getCurrentGeoframe(), mx - i, my + j, this.options.maxdist);
                if (GeometryUtils.CalcDistance(refpos, pos) < thresh && !pos.warning) {
                    this.coordinates.push(pos);
                    p += 1;
                }
            }
            if (p == 0)
                break;
        }
        for (i = 0; i < limit; i += increment) {
            p = 0;
            for (j = 0; j < limit; j += increment) {
                pos = this.scene
                    .getMeasureHandler()
                    .createMonoplotMeasurePoint(this.scene.getCurrentGeoframe(), mx + i, my - j, this.options.maxdist);
                if (GeometryUtils.CalcDistance(refpos, pos) < thresh && !pos.warning) {
                    this.coordinates.push(pos);
                    p += 1;
                }
            }
            if (p == 0)
                break;
        }
        for (i = 0; i < limit; i += increment) {
            p = 0;
            for (j = 0; j < limit; j += increment) {
                pos = this.scene
                    .getMeasureHandler()
                    .createMonoplotMeasurePoint(this.scene.getCurrentGeoframe(), mx - i, my - j, this.options.maxdist);
                if (GeometryUtils.CalcDistance(refpos, pos) < thresh && !pos.warning) {
                    this.coordinates.push(pos);
                    p += 1;
                }
            }
            if (p == 0)
                break;
        }
        this._draw(this.coordinates);
        this.mousemoveactive = false;
    }
    /**
     * Draws the feature into the scene
     * @method _draw
     * @param {MeasurePoint[]} coors the geometry coordinates
     * @private
     */
    _draw(coors) {
        let color = this.options.layerstyle.getRawStyle(null).pointcolor;
        let alpha = 0.6;
        if (isDefined(color[3]))
            alpha = color[3];
        const coorsarray = [];
        for (let i = 0; i < coors.length; i++) {
            if (this.options.quality) {
                if (coors[i].quality > 191) {
                    color = [0, 1, 0, alpha];
                }
                else if (coors[i].quality > 127) {
                    color = [1, 1, 0, alpha];
                }
                else if (coors[i].quality > 63) {
                    color = [1, 0, 0, alpha];
                }
            }
            coorsarray.push([coors[i].Easting, coors[i].Northing, coors[i].Height, color[0], color[1], color[2]]);
        }
        //Update the features for drawing
        this.multipointfeature.geometry.coordinates = coorsarray;
        if (this.actualPointCloud)
            this.scene.infrascene.remove(this.actualPointCloud);
        if (coorsarray.length > 0) {
            this.actualPointCloud = new SimplePointCloud(coorsarray);
            this.scene.infrascene.add(this.actualPointCloud);
        }
    }
    /**
     * Draws the feature into the scene
     * @method _drawFrame
     * @param {MeasurePoint[]} coors the geometry coordinates
     * @private
     */
    _drawFrame(coors) {
        const coorsarray = [];
        for (let i = 0; i < coors.length; i++) {
            coorsarray.push([coors[i].Easting, coors[i].Northing, coors[i].Height]);
        }
        //Update the features for drawing
        this.multipointfeatureframe.geometry.coordinates = coorsarray;
        this.featurecollectionframe.update();
    }
    /**
     * removes all drawing from the scene
     * @method _cleanUp
     * @private
     */
    _cleanUp() {
        if (this.coordinates.length > 0)
            this.coordinates = [];
        this.multipointfeature.geometry.clear();
        this.featurecollection.update();
    }
    /**
     * removes all drawing from the scene
     * @method reset
     * @public
     */
    reset() {
        this._cleanUp();
        this.toolstate = 0;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.MOUSEMOVE, this.mousemoveeventnr);
        this.mousemoveeventnr = null;
    }
}
PointCloudViewTool.enabled = "enabled";
PointCloudViewTool.disabled = "disabled";
//# sourceMappingURL=pointcloudviewtool.js.map